import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Drawer, FormControlLabel,
  Checkbox, IconButton, InputAdornment, Divider, Stack, Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { toast } from 'react-toastify';

const GreenCheckbox = styled(Checkbox)(() => ({
  color: '#CBCBCB',
  padding: '5px 8px',
  '&.Mui-checked': { color: '#065F46' },
}));

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    fontFamily: 'Heebo',
    '& fieldset': { borderColor: '#E7E7E7' },
    '&:hover fieldset': { borderColor: '#D7D7D7' },
    '&.Mui-focused fieldset': { borderColor: '#065F46' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Heebo' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#065F46' },
  '& .MuiFormHelperText-root': { fontFamily: 'Heebo', marginLeft: '4px', fontSize: '11px' },
};

// Indices 9–11 are new. Keep order consistent with backend mapping.
const PERMISSION_SECTIONS = [
  {
    label: 'Core Administration',
    items: [{ label: 'Dashboard', index: 0 }],
  },
  {
    label: 'Management',
    items: [
      { label: 'User Management', index: 1 },
      { label: 'Vendor Management', index: 2 },
      { label: 'Orders', index: 3 },
      { label: 'Event Management', index: 6 },
    ],
  },
  {
    label: 'Content & Communication',
    items: [
      { label: 'Content Management', index: 4 },
      { label: 'Push Notification', index: 7 },
      { label: 'Blog', index: 9, isNew: true },
      { label: 'SEO', index: 11, isNew: true },
    ],
  },
  {
    label: 'Analytics & Support',
    items: [
      { label: 'Reports & Analysis', index: 5 },
      { label: 'Help & Support', index: 8 },
      { label: 'Feedbacks', index: 10, isNew: true },
    ],
  },
];

const TOTAL_PERMISSIONS = 12;

const EMPTY = {
  userName: '',
  email: '',
  mobile: '',
  password: '',
  retypePassword: '',
  permissions: Array(TOTAL_PERMISSIONS).fill(false),
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UserPermissionModal = ({ open, onClose, onSave, editingData }) => {
  const [data, setData] = useState(EMPTY);
  const [showPw, setShowPw] = useState({ password: false, retypePassword: false });
  const [touched, setTouched] = useState({});
  const [tried, setTried] = useState(false);

  const reset = () => {
    setData(EMPTY);
    setTouched({});
    setTried(false);
    setShowPw({ password: false, retypePassword: false });
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  // Guard after all hooks: EditUserPermission handles editing; this drawer only creates.
  if (editingData) return null;

  const handleInput = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      if (/^\d{0,10}$/.test(value)) setData((p) => ({ ...p, [name]: value }));
    } else {
      if (value.trim() !== '' || value === '') setData((p) => ({ ...p, [name]: value }));
    }
  };

  const handleBlur = (field) => setTouched((p) => ({ ...p, [field]: true }));

  const togglePerm = (index) => {
    setData((p) => {
      const updated = [...p.permissions];
      updated[index] = !updated[index];
      return { ...p, permissions: updated };
    });
  };

  const toggleSection = (items, forceOn) => {
    setData((p) => {
      const updated = [...p.permissions];
      items.forEach(({ index }) => { updated[index] = forceOn; });
      return { ...p, permissions: updated };
    });
  };

  const emailValid = emailRegex.test(data.email);
  const pwMatch = data.password === data.retypePassword;
  const pwLong = data.password.length >= 8;
  const hasAnyPerm = data.permissions.some(Boolean);

  const getErr = (field) => {
    if (!(touched[field] || tried)) return '';
    switch (field) {
      case 'userName': return !data.userName ? 'Username is required' : '';
      case 'email': return !data.email ? 'Email is required' : !emailValid ? 'Enter a valid email' : '';
      case 'mobile': return !data.mobile ? 'Mobile is required' : data.mobile.length !== 10 ? 'Must be exactly 10 digits' : '';
      case 'password': return !data.password ? 'Password is required' : !pwLong ? 'Minimum 8 characters' : '';
      case 'retypePassword': return !data.retypePassword ? 'Please confirm your password' : !pwMatch ? 'Passwords do not match' : '';
      default: return '';
    }
  };

  const handleSave = () => {
    setTried(true);
    if (!data.userName || !data.email || !data.mobile || !data.password || !data.retypePassword) {
      toast.warning('All fields are required!');
      return;
    }
    if (data.mobile.length !== 10) { toast.error('Mobile number must be 10 digits!'); return; }
    if (!emailValid) { toast.error('Please enter a valid email address.'); return; }
    if (!pwMatch) { toast.error('Passwords do not match!'); return; }
    if (!pwLong) { toast.error('Password must be at least 8 characters long!'); return; }
    if (!hasAnyPerm) { toast.error('Please select at least one permission.'); return; }

    onSave({
      username: data.userName,
      email: data.email,
      phone: data.mobile,
      password: data.password,
      retypePassword: data.retypePassword,
      permissions: data.permissions,
    });

    reset();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          background: '#fff',
          borderTopLeftRadius: { sm: '12px' },
          borderBottomLeftRadius: { sm: '12px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* HEADER */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F1F1F1', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.3 }}>
            Add New Permission
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#8B8B8B', mt: 0.4, fontFamily: 'Heebo' }}>
            Create a sub-admin account with access controls
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ border: '1px solid #EBEBEB', width: 36, height: 36, borderRadius: '6px', flexShrink: 0, ml: 1 }}>
          <CloseIcon sx={{ fontSize: 18, color: '#362B24' }} />
        </IconButton>
      </Box>

      {/* BODY */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>

        {/* Account Details */}
        <SectionHeading>Account Details</SectionHeading>
        <Stack spacing={2} mb={3}>
          <TextField
            fullWidth size="small" label="Username" name="userName"
            value={data.userName}
            onChange={handleInput}
            onBlur={() => handleBlur('userName')}
            error={!!getErr('userName')} helperText={getErr('userName')}
            sx={inputSx}
          />
          <TextField
            fullWidth size="small" label="Email Address" name="email" type="email"
            value={data.email}
            onChange={handleInput}
            onBlur={() => handleBlur('email')}
            error={!!getErr('email')} helperText={getErr('email')}
            sx={inputSx}
          />
          <TextField
            fullWidth size="small" label="Mobile Number" name="mobile"
            value={data.mobile}
            onChange={handleInput}
            onBlur={() => handleBlur('mobile')}
            error={!!getErr('mobile')}
            helperText={getErr('mobile') || '10-digit number'}
            sx={inputSx}
          />
        </Stack>

        <Divider sx={{ mb: 3, borderColor: '#F4F4F4' }} />

        {/* Security */}
        <SectionHeading>Security</SectionHeading>
        <Stack spacing={2} mb={3}>
          <TextField
            fullWidth size="small" label="Password" name="password"
            type={showPw.password ? 'text' : 'password'}
            value={data.password}
            onChange={handleInput}
            onBlur={() => handleBlur('password')}
            error={!!getErr('password')}
            helperText={getErr('password') || 'Minimum 8 characters'}
            sx={inputSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPw((p) => ({ ...p, password: !p.password }))}>
                    {showPw.password
                      ? <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#8B8B8B' }} />
                      : <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: '#8B8B8B' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth size="small" label="Confirm Password" name="retypePassword"
            type={showPw.retypePassword ? 'text' : 'password'}
            value={data.retypePassword}
            onChange={handleInput}
            onBlur={() => handleBlur('retypePassword')}
            error={!!getErr('retypePassword')} helperText={getErr('retypePassword')}
            sx={inputSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPw((p) => ({ ...p, retypePassword: !p.retypePassword }))}>
                    {showPw.retypePassword
                      ? <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#8B8B8B' }} />
                      : <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: '#8B8B8B' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Divider sx={{ mb: 3, borderColor: '#F4F4F4' }} />

        {/* Module Access */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <SectionHeading noMargin>Module Access</SectionHeading>
          {tried && !hasAnyPerm && (
            <Typography sx={{ fontSize: '11px', color: '#EA001B', fontFamily: 'Heebo' }}>
              Select at least one module
            </Typography>
          )}
        </Box>

        <Stack spacing={2}>
          {PERMISSION_SECTIONS.map((section) => {
            const allOn = section.items.every(({ index }) => data.permissions[index]);
            return (
              <Box
                key={section.label}
                sx={{ border: '1px solid #EBEBEB', borderRadius: '8px', p: 2, background: '#FAFAFA' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#362B24', fontFamily: 'Heebo', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {section.label}
                  </Typography>
                  <Typography
                    onClick={() => toggleSection(section.items, !allOn)}
                    sx={{ fontSize: '12px', color: '#065F46', cursor: 'pointer', fontFamily: 'Heebo', userSelect: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {allOn ? 'Deselect all' : 'Select all'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {section.items.map(({ label, index, isNew }) => (
                    <Box key={index} sx={{ width: '50%', minWidth: 140 }}>
                      <FormControlLabel
                        control={
                          <GreenCheckbox
                            size="small"
                            checked={data.permissions[index] || false}
                            onChange={() => togglePerm(index)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '13px', fontFamily: 'Heebo', color: '#362B24' }}>
                              {label}
                            </Typography>
                            {isNew && (
                              <Chip
                                label="New"
                                size="small"
                                sx={{ height: 16, fontSize: '9px', bgcolor: '#E8F5E9', color: '#065F46', fontFamily: 'Heebo', px: 0.2 }}
                              />
                            )}
                          </Box>
                        }
                        sx={{ mx: 0, py: 0.3 }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* FOOTER */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #F1F1F1', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0, background: '#fff' }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: '8px', height: '41px', px: 3, border: '1px solid #EBEBEB', color: '#362B24', fontFamily: 'Heebo', textTransform: 'none', fontSize: '14px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          sx={{
            borderRadius: '8px', height: '41px', px: 4,
            background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)',
            color: '#fff', fontFamily: 'Heebo', fontWeight: 600, textTransform: 'none', fontSize: '14px',
            boxShadow: '1px 2px 30px 0px rgba(0,0,0,0.06)',
            '&:hover': { opacity: 0.9, background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)' },
          }}
        >
          Add User
        </Button>
      </Box>
    </Drawer>
  );
};

function SectionHeading({ children, noMargin }) {
  return (
    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#065F46', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Heebo', mb: noMargin ? 0 : 2 }}>
      {children}
    </Typography>
  );
}

export default UserPermissionModal;
